import React from 'react';
import { X } from 'lucide-react';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import LucideIcon from '../lucide-icon';
import { getIconsDefinitions } from '../lucide-icon/icons-definitions';

type UIVariant = React.ComponentProps<typeof Button>['variant'];
type UISize = React.ComponentProps<typeof Button>['size'];

const VARIANTS: Record<string, UIVariant> = {
  primary: 'default',
  secondary: 'secondary',
  danger: 'destructive',
  success: 'success',
  warning: 'warning',
  info: 'info',
  link: 'link',
  light: 'secondary',
  white: 'outline',
  default: 'ghost',
  'outline-primary': 'outline',
  'outline-secondary': 'outline',
};

const OUTLINE_COLORS: Record<string, string> = {
  danger:
    'tw:border-destructive tw:text-destructive tw:hover:bg-destructive/10 tw:hover:text-destructive',
  success:
    'tw:border-success tw:text-success tw:hover:bg-success/10 tw:hover:text-success',
  warning:
    'tw:border-warning tw:text-warning tw:hover:bg-warning/10 tw:hover:text-warning',
  info: 'tw:border-info tw:text-info tw:hover:bg-info/10 tw:hover:text-info',
};

const SIZES = new Set<string | undefined>(['xs', 'sm', 'lg']);
const ICON_SIZES = new Set<string | undefined>([
  'icon',
  'icon-xs',
  'icon-sm',
  'icon-lg',
]);

const ICON_STROKE_WIDTH = 1.75;

type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'info'
  | 'link'
  | 'light'
  | 'white'
  | 'default'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-danger'
  | 'outline-success'
  | 'outline-warning'
  | 'outline-info';

type ButtonSize =
  | 'xs'
  | 'sm'
  | 'lg'
  | 'icon'
  | 'icon-xs'
  | 'icon-sm'
  | 'icon-lg';

type IconProp = string | React.ReactElement | React.ElementType;

type ButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'color'> & {
  /** Cor do botão. `outline-primary` e `outline-secondary` dão o contorno neutro; os outros `outline-*` mantêm a cor. `default` é o botão sem fundo. */
  color?: ButtonColor;
  /** Contorno com a cor do `color` (o mesmo que `outline-<cor>`). */
  outline?: boolean;
  /** Tamanho. Botão só de ícone usa `icon` (ou `icon-xs`, `icon-sm`, `icon-lg`). */
  size?: ButtonSize;
  /** Ícone antes do texto: nome do `LucideIcon` (ex.: `'Plus'`), elemento ou componente. */
  leftIcon?: IconProp;
  /** Ícone depois do texto: nome do `LucideIcon`, elemento ou componente. */
  rightIcon?: IconProp;
  /** Props do ícone da esquerda (ex.: `{ size: 14 }`). */
  leftIconProps?: object;
  /** Props do ícone da direita. */
  rightIconProps?: object;
  /** Botão de barra de ferramentas: mostra o `icon` (e ignora `leftIcon`/`rightIcon`); sem texto, escolhe sozinho o tamanho de ícone. */
  toolbar?: boolean;
  /** Ícone do botão com `toolbar`: nome do `LucideIcon`, elemento ou componente. */
  icon?: IconProp;
  /** Ocupa a largura toda. */
  block?: boolean;
  /** Vira link com a aparência de botão. */
  href?: string;
  /** Elemento no lugar do `<button>` (ex.: `Link` do router). */
  tag?: React.ElementType;
  /** Botão "X" de fechar, sem fundo. */
  close?: boolean;
  /** Ref do botão (o mesmo que `ref`). */
  innerRef?: React.Ref<HTMLButtonElement>;
  /** Ignorada: o tamanho do ícone vem do `size`. */
  iconSize?: unknown;
  /** Ignorada: herança do reactstrap. */
  active?: unknown;
  /** Ignorada: herança do reactstrap. */
  cssModule?: unknown;
  /** Ignorada: herança do reactstrap. Use `type`. */
  htmlType?: unknown;
  /** Ignorada: herança do reactstrap. */
  bsSize?: unknown;
  /** Ignorada: herança do reactstrap. */
  bsStyle?: unknown;
  /** Ignorada: use `disabled`. */
  isDisabled?: unknown;
  /** Ignorada: a cor vem só do `color`. */
  variant?: unknown;
};

function getSize(size?: string, iconOnly?: boolean) {
  if (ICON_SIZES.has(size)) return size as UISize;
  const base = SIZES.has(size) ? size : 'default';
  if (!iconOnly) return base as UISize;
  return (base === 'default' ? 'icon' : `icon-${base}`) as UISize;
}

function normalizeColor(color: string, outline?: boolean): [string, boolean] {
  const name = String(color || 'primary');
  if (name.startsWith('outline-')) return [name.slice(8), true];
  return [name, Boolean(outline)];
}

// O SVG precisa ser filho direto do botão para o ui/button ajustar o
// espaçamento (has-[>svg]), por isso o ícone por nome não usa o LucideIcon.
function renderNamedIcon(name: string, iconProps: object) {
  const Icon = getIconsDefinitions(name);
  return Icon ? <Icon strokeWidth={ICON_STROKE_WIDTH} {...iconProps} /> : null;
}

function renderIcon(icon: IconProp | undefined, iconProps: object = {}) {
  if (!icon) return null;

  if (typeof icon === 'string') {
    return renderNamedIcon(icon, iconProps);
  }

  if (React.isValidElement<Record<string, unknown>>(icon)) {
    if (icon.type === LucideIcon) {
      const {
        icon: name,
        colorHover,
        ...elementProps
      } = icon.props as { icon: string; colorHover?: unknown };
      return renderNamedIcon(name, { ...elementProps, ...iconProps });
    }
    return React.cloneElement(icon, iconProps);
  }

  const IconComponent = icon as React.ElementType;
  return <IconComponent {...iconProps} />;
}

function hasContent(children: React.ReactNode) {
  return React.Children.toArray(children).some((child) => child !== '');
}

/**
 * Botão do portal. A cor vem de `color` (não há `variant`); ícone com texto usa `leftIcon`/`rightIcon`, e botão só de ícone usa `size="icon"` ou `toolbar icon="..."`. Com `onClick` e sem `type`, sai `type="button"`.
 * @import import Button from 'src/componentes/button';
 */
const ButtonComponent = React.forwardRef(function ButtonComponent(
  {
    toolbar,
    icon,
    leftIcon,
    rightIcon,
    color = 'primary',
    outline,
    size,
    block,
    tag,
    href,
    close,
    leftIconProps,
    rightIconProps,
    innerRef,
    className,
    children,
    iconSize,
    active,
    cssModule,
    htmlType,
    bsSize,
    bsStyle,
    isDisabled,
    variant,
    ...other
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const buttonRef = innerRef || ref;

  if (close) {
    return (
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon-sm"
        aria-label="Close"
        className={className}
        {...other}
        type={other.type ?? 'button'}
      >
        <X />
      </Button>
    );
  }

  const [colorName, isOutline] = normalizeColor(color, outline);
  const uiVariant = isOutline ? 'outline' : VARIANTS[colorName] || 'default';
  const iconOnly = toolbar && !hasContent(children);

  const content = (
    <>
      {toolbar ? renderIcon(icon) : renderIcon(leftIcon, leftIconProps)}
      {children}
      {toolbar ? null : renderIcon(rightIcon, rightIconProps)}
    </>
  );

  const buttonProps = {
    ref: buttonRef,
    variant: uiVariant,
    size: getSize(size, iconOnly),
    className: cn(
      isOutline && OUTLINE_COLORS[colorName],
      block && 'tw:flex tw:w-full',
      className
    ),
  };

  if (href || tag) {
    const Tag: React.ElementType = tag || 'a';
    const { disabled, type, ...elementProps } = other;
    return (
      <Button {...buttonProps} asChild>
        <Tag
          href={href}
          aria-disabled={disabled || undefined}
          {...elementProps}
          className={cn(disabled && 'tw:pointer-events-none tw:opacity-50')}
        >
          {content}
        </Tag>
      </Button>
    );
  }

  return (
    <Button
      {...buttonProps}
      {...other}
      type={other.type ?? (other.onClick ? 'button' : undefined)}
    >
      {content}
    </Button>
  );
});

export default ButtonComponent;
